// frontend/src/pages/MetaCubeQuantiquePage.jsx
import React, { useState, useRef, useEffect } from "react";
import MetaCubeQuantiqueVisualizer from "../components/MetaCubeQuantiqueVisualizer";
import useMetaCubeQuantiqueVisualizer from "../hooks/useMetaCubeQuantiqueVisualizer";
import "../styles/metacube_quantique.css"; // ✅ Style quantique

export default function MetaCubeQuantiquePage() {
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);

  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  const [fractalDepth, setFractalDepth] = useState(50);
  const [quantumFlux, setQuantumFlux] = useState(50);
  const [velocityFactor, setVelocityFactor] = useState(50);

  // 🔹 Nouveaux curseurs
  const [turbulenceFactor, setTurbulenceFactor] = useState(50);
  const [pulseIntensity, setPulseIntensity] = useState(50);

  const containerRef = useRef(null);

  // --- Hook visualizer (frames SSE depuis backend quantique)
  const { framesBuffer, entropyDyn: entropyDynHook } = useMetaCubeQuantiqueVisualizer({
    running,
    chaos,
    glow,
    fractalDepth,
    quantumFlux,
    velocityFactor,
    turbulenceFactor,
    pulseIntensity,
  });

  // ✅ Sync local entropyDyn avec hook
  useEffect(() => {
    if (entropyDynHook !== undefined && entropyDynHook !== null) {
      setEntropyDyn(entropyDynHook);
    }
  }, [entropyDynHook]);

  // --- Entropie UI (curseurs → backend quantique)
  useEffect(() => {
    const fetchEntropyUI = async () => {
      try {
        setLoadingUI(true);
        const res = await fetch("/api/geometry/metacube_quantique/entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chaos,
            glow,
            fractal_depth: fractalDepth / 100,
            quantum_flux: quantumFlux / 100,
            velocity_factor: velocityFactor / 100,
            turbulence_factor: turbulenceFactor / 100, // 🔹 nouveau
            pulse_intensity: pulseIntensity / 100,     // 🔹 nouveau
            running,
          }),
        });
        const data = await res.json();
        if (data.entropy !== undefined) setEntropyUI(data.entropy);
      } catch (err) {
        console.error("Erreur fetch entropy UI MetaCubeQuantique:", err);
        setEntropyUI(null);
      } finally {
        setLoadingUI(false);
      }
    };
    fetchEntropyUI();
  }, [
    chaos,
    glow,
    fractalDepth,
    quantumFlux,
    velocityFactor,
    turbulenceFactor,
    pulseIntensity,
    running,
  ]);

  // ⚡ Score total
  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  return (
    <div className="metacube-quantique-container">
      <h1 className="quantique-title">MetaCube Quantique Visualizer</h1>

      {/* Jauges */}
      <div className="gauges-row">
        <div className="card small">
          <h2>Entropie UI</h2>
          <div className="value ui">
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card small">
          <h2 className="dyn-title">Entropie Dynamique</h2>
          <div className="value dyn">
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card small">
          <h2 className="total-title">Score Total</h2>
          <div className="value total">{entropyTotal}</div>
        </div>
      </div>

      {/* Contrôles + Visualizer */}
      <div className="controls-row">
        <div className="controls-panel">
          <button
            className="generate-button"
            onClick={() => setRunning((p) => !p)}
          >
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            onClick={() => setChaos((p) => !p)}
            className={chaos ? "toggle-button active-chaos" : "toggle-button"}
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            onClick={() => setGlow((p) => !p)}
            className={glow ? "toggle-button active-glow" : "toggle-button"}
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          <div className="input-group">
            <label>Fractal Depth : {fractalDepth}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={fractalDepth}
              onChange={(e) => setFractalDepth(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Quantum Flux : {quantumFlux}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={quantumFlux}
              onChange={(e) => setQuantumFlux(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Velocity Factor : {velocityFactor}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={velocityFactor}
              onChange={(e) => setVelocityFactor(Number(e.target.value))}
            />
          </div>

          {/* 🔹 Nouveau curseur Turbulence */}
          <div className="input-group">
            <label>Turbulence Factor : {turbulenceFactor}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={turbulenceFactor}
              onChange={(e) => setTurbulenceFactor(Number(e.target.value))}
            />
          </div>

          {/* 🔹 Nouveau curseur Pulse */}
          <div className="input-group">
            <label>Pulse Intensity : {pulseIntensity}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={pulseIntensity}
              onChange={(e) => setPulseIntensity(Number(e.target.value))}
            />
          </div>
        </div>

        <div ref={containerRef} className="stream-canvas">
          <MetaCubeQuantiqueVisualizer
            containerRef={containerRef}
            running={running}
            chaos={chaos}
            glow={glow}
            fractalDepth={fractalDepth}
            quantumFlux={quantumFlux}
            velocityFactor={velocityFactor}
            turbulenceFactor={turbulenceFactor} // 🔹
            pulseIntensity={pulseIntensity}     // 🔹
            framesBuffer={framesBuffer}
          />
        </div>
      </div>
    </div>
  );
}
